import React, { useEffect, useState } from 'react';
import { X, Smartphone, Mail, Send, BellRing, Moon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { enablePush, isPushActiveHere, disablePushHere, pushNeedsInstall, pushSupported, permission, isIOS } from '../utils/pwa';

const CHANNEL_ICONS = { app: BellRing, push: Smartphone, email: Mail, telegram: Send };

export default function NotificationSettings({ onClose }) {
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [email, setEmail] = useState('');
  const [pushHere, setPushHere] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/notifications/settings').then(r => { setData(r.data); setPrefs(r.data.prefs); setEmail(r.data.notify_email); }).catch(() => toast.error('No se pudo cargar'));
  useEffect(() => { load(); isPushActiveHere().then(setPushHere).catch(() => {}); }, []);

  if (!data || !prefs) return null;
  const av = data.availability;
  const channelEnabled = (key) => key === 'app' || (key === 'push' && av.push.available) || (key === 'email' && av.email.available) || (key === 'telegram' && av.telegram.available);
  const channels = data.channels.filter(c => channelEnabled(c.key));
  const toggle = (ev, ch) => setPrefs({ ...prefs, events: { ...prefs.events, [ev]: { ...prefs.events[ev], [ch]: !prefs.events[ev][ch] } } });

  const activatePush = async () => {
    const r = await enablePush().catch(() => 'error');
    if (r === 'granted') { toast.success('Notificaciones activadas en este dispositivo'); setPushHere(true); load(); }
    else if (r === 'needs_install') toast('En iPhone primero agregá la app a tu pantalla de inicio y abrila desde ese ícono.', { duration: 7000 });
    else if (r === 'denied') toast.error('Bloqueaste las notificaciones. Habilitalas desde la configuración del navegador para este sitio.', { duration: 7000 });
    else if (r !== 'default') toast.error('Este navegador no permite notificaciones');
  };
  const deactivatePush = async () => { await disablePushHere().catch(() => {}); setPushHere(false); load(); toast.success('Desactivadas en este dispositivo'); };
  const linkTelegram = async () => {
    try { const r = await api.post('/notifications/telegram/link'); window.open(r.data.url, '_blank', 'noopener'); toast('Tocá "Iniciar" en Telegram y volvé acá.', { duration: 6000 }); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };
  const unlinkTelegram = async () => { await api.delete('/notifications/telegram').catch(() => {}); load(); };
  const save = async () => {
    setSaving(true);
    try { await api.put('/notifications/settings', { prefs, notify_email: email }); toast.success('Preferencias guardadas'); onClose(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };
  const test = async () => {
    try { await api.put('/notifications/settings', { prefs, notify_email: email }); await api.post('/notifications/test'); toast.success('Enviamos una notificación de prueba'); }
    catch { toast.error('No se pudo enviar la prueba'); }
  };

  const section = { border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 12 };
  const title = { fontSize: 13, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 };
  const muted = { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 };
  const hasEvent = (key) => data.events.some(e => e.key === key);

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0 }}>Notificaciones</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 6 }}><X size={18} /></button>
        </div>

        {/* Este dispositivo */}
        {av.push.available && (
          <div style={section}>
            <div style={title}><Smartphone size={15} /> En este dispositivo</div>
            {pushHere ? (<>
              <div style={muted}>Las notificaciones están activadas en este dispositivo.</div>
              <button className="btn btn-ghost btn-sm" onClick={deactivatePush} style={{ marginTop: 8 }}>Desactivar en este dispositivo</button>
            </>) : pushNeedsInstall() ? (
              <div style={muted}>En iPhone las notificaciones solo funcionan con la app instalada: tocá <b>Compartir</b> <span aria-hidden>⬆️</span> y luego <b>"Agregar a inicio"</b>. Después abrí Udream App desde ese ícono y activalas acá.</div>
            ) : !pushSupported() ? (
              <div style={muted}>Este navegador no admite notificaciones.</div>
            ) : permission() === 'denied' ? (
              <div style={muted}>Las notificaciones están bloqueadas para este sitio. Habilitalas desde la configuración del navegador (ícono del candado junto a la dirección).</div>
            ) : (<>
              <div style={muted}>Recibí avisos aunque la app esté cerrada. Cuando lo actives, el {isIOS() ? 'iPhone' : 'navegador'} te va a pedir permiso: tocá <b>"Permitir"</b>.</div>
              <button className="btn btn-primary btn-sm" onClick={activatePush} style={{ marginTop: 8 }}><BellRing size={14} /> Activar notificaciones</button>
            </>)}
          </div>
        )}

        {/* Email y Telegram */}
        {(av.email.available || av.telegram.available) && (
          <div style={{ ...section, display: 'grid', gap: 12 }}>
            {av.email.available && (
              <div>
                <div style={title}><Mail size={15} /> Email</div>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" style={{ fontSize: 13 }} />
              </div>
            )}
            {av.telegram.available && (
              <div>
                <div style={title}><Send size={15} /> Telegram</div>
                {av.telegram.linked
                  ? <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}><span style={{ ...muted, color: 'var(--success)' }}>Conectado</span><button className="btn btn-ghost btn-sm" onClick={unlinkTelegram}>Desconectar</button></div>
                  : <><div style={muted}>Recibí los avisos en Telegram.</div><button className="btn btn-secondary btn-sm" onClick={linkTelegram} style={{ marginTop: 6 }}><Send size={14} /> Conectar Telegram</button></>}
              </div>
            )}
          </div>
        )}

        {/* Eventos x canales */}
        <div style={section}>
          <div style={title}>¿Qué querés recibir y por dónde?</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <th style={{ textAlign: 'left', padding: '6px 4px', fontSize: 11, color: 'var(--text-muted)' }}>Aviso</th>
                {channels.map(c => { const I = CHANNEL_ICONS[c.key]; return <th key={c.key} title={c.label} style={{ padding: '6px 4px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', whiteSpace: 'nowrap' }}><I size={13} style={{ verticalAlign: 'middle' }} /> <span className="notif-ch-label">{c.label}</span></th>; })}
              </tr></thead>
              <tbody>{data.events.map(ev => (
                <tr key={ev.key} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 4px' }}><div style={{ fontWeight: 600 }}>{ev.label}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ev.help}</div></td>
                  {channels.map(c => (
                    <td key={c.key} style={{ textAlign: 'center', padding: '8px 4px' }}>
                      <input type="checkbox" checked={!!prefs.events[ev.key]?.[c.key]} onChange={() => toggle(ev.key, c.key)} style={{ width: 17, height: 17, cursor: 'pointer' }} />
                    </td>
                  ))}
                </tr>
              ))}</tbody>
            </table>
          </div>
          {hasEvent('unanswered') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
              Avisar "sin responder" después de
              <select value={prefs.unanswered_minutes} onChange={e => setPrefs({ ...prefs, unanswered_minutes: parseInt(e.target.value, 10) })} style={{ width: 'auto', fontSize: 13 }}>
                {[5, 10, 15, 30, 60, 120].map(m => <option key={m} value={m}>{m < 60 ? m + ' min' : m / 60 + ' h'}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Silencio */}
        <div style={section}>
          <label style={{ ...title, cursor: 'pointer', marginBottom: 0 }}>
            <input type="checkbox" checked={prefs.quiet.enabled} onChange={e => setPrefs({ ...prefs, quiet: { ...prefs.quiet, enabled: e.target.checked } })} style={{ width: 16, height: 16 }} />
            <Moon size={15} /> No molestar por la noche
          </label>
          {prefs.quiet.enabled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, flexWrap: 'wrap' }}>
              De <input type="time" value={prefs.quiet.start} onChange={e => setPrefs({ ...prefs, quiet: { ...prefs.quiet, start: e.target.value } })} style={{ width: 'auto' }} />
              a <input type="time" value={prefs.quiet.end} onChange={e => setPrefs({ ...prefs, quiet: { ...prefs.quiet, end: e.target.value } })} style={{ width: 'auto' }} />
              <span style={muted}>(los avisos quedan igual dentro de la app)</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={test}>Enviar prueba</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </div>
        <style>{`@media (max-width: 560px) { .notif-ch-label { display: none; } }`}</style>
      </div>
    </div>
  );
}
